import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, Settings2 } from "lucide-react";
import { Switch } from "../ui/switch";

const KEY = "mfmed_cookie_consent";

export default function CookieBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, statistics: false, marketing: false });

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    if (!localStorage.getItem(KEY)) setVisible(true);
  }, [location.pathname]);

  useEffect(() => {
    const reopen = () => {
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
        if (saved.categories) setPrefs({ necessary: true, statistics: !!saved.categories.statistics, marketing: !!saved.categories.marketing });
      } catch (e) { /* ignore malformed consent */ }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("mfmed:open-cookie", reopen);
    return () => window.removeEventListener("mfmed:open-cookie", reopen);
  }, []);

  const save = (categories) => {
    localStorage.setItem(KEY, JSON.stringify({ categories, ts: new Date().toISOString() }));
    setVisible(false);
  };

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 140, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 140, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[70] p-3 sm:p-5"
        >
          <div className="mf-container">
            <div className="relative bg-[var(--mf-ink)] text-white border-t-2 border-[var(--mf-red)] shadow-2xl">
              <div className="p-6 lg:p-8">
                <div className="grid lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-start">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <Cookie size={20} className="text-[var(--mf-red)]" />
                      <h2 className="font-head font-semibold text-lg">Datenschutz &amp; Cookies</h2>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-white/70 max-w-3xl">
                      Wir verwenden ausschließlich technisch notwendige Cookies, damit diese Website
                      einwandfrei funktioniert. Optionale Cookies (z.&nbsp;B. für Statistik oder Marketing)
                      setzen wir nur mit Ihrer Einwilligung. Sie können Ihre Auswahl jederzeit widerrufen.
                      Weitere Informationen finden Sie in unserer{" "}
                      <Link to="/datenschutz" className="underline underline-offset-2 hover:text-white">Datenschutzerklärung</Link>
                      {" "}sowie im{" "}
                      <Link to="/impressum" className="underline underline-offset-2 hover:text-white">Impressum</Link>.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0 w-full lg:w-56">
                    <button type="button" data-testid="cookie-accept-all" onClick={() => save({ necessary: true, statistics: true, marketing: true })} className="mf-cta w-full justify-center">
                      Alle akzeptieren
                    </button>
                    <button type="button" data-testid="cookie-necessary" onClick={() => save({ necessary: true, statistics: false, marketing: false })} className="w-full justify-center inline-flex items-center px-6 py-3 border border-white/25 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
                      Nur notwendige
                    </button>
                    <button type="button" data-testid="cookie-settings-toggle" onClick={() => setShowSettings((v) => !v)} className="w-full justify-center inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors py-1">
                      <Settings2 size={15} /> Einstellungen
                    </button>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {showSettings && (
                    <motion.div
                      data-testid="cookie-settings-panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-6 pt-6 border-t border-white/15 grid sm:grid-cols-3 gap-4">
                        {[
                          { key: "necessary", title: "Notwendig", desc: "Für den Betrieb der Website erforderlich. Immer aktiv.", locked: true },
                          { key: "statistics", title: "Statistik", desc: "Anonyme Auswertung der Nutzung zur Verbesserung." },
                          { key: "marketing", title: "Marketing", desc: "Inhalte und Reichweitenmessung externer Dienste." },
                        ].map((c) => (
                          <div key={c.key} className="bg-white/[0.04] border border-white/10 p-4">
                            <div className="flex items-center justify-between">
                              <span className="font-head font-semibold text-sm">{c.title}</span>
                              <Switch
                                data-testid={`cookie-switch-${c.key}`}
                                checked={c.locked ? true : prefs[c.key]}
                                disabled={c.locked}
                                onCheckedChange={(v) => !c.locked && setPrefs((p) => ({ ...p, [c.key]: v }))}
                              />
                            </div>
                            <p className="mt-2 text-xs leading-relaxed text-white/55">{c.desc}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 flex justify-end">
                        <button type="button" data-testid="cookie-save-selection" onClick={() => save({ ...prefs, necessary: true })} className="mf-cta text-sm py-2.5 px-6 justify-center">
                          Auswahl speichern
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
